// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — onglet « Comptabilité » (DÉMONSTRATION, plan non validé) :
//   • écritures (paginées, filtre journal et état ; lignes en lecture ; validation par une autre personne que
//     l'auteur ; contrepassation proposée, jamais de modification) ;
//   • balance par compte ;
//   • exercices et périodes : clôture en deux temps (demande, puis confirmation ou refus par une autre personne ;
//     irréversible), pour le mois comme pour l'exercice ;
//   • mise en place (étape 5, MiseEnPlaceFinance.jsx) : plan de démonstration, ouverture d'exercice, écriture manuelle
//     préparée avec sa pièce et son équilibre affiché, abandon motivé d'un brouillon, validation avec pièce.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BookOpen, BookPlus, Lock, Scale, Trash2, Undo2 } from 'lucide-react';
import { Bouton, BandeauInfo } from '../../design/composants';
import { toArray, libelle } from './api';
import { Etat, PaginationDs } from './commun';
import { useSocial, francs, dateLongue, moisLong, ETATS_ECRITURE, ETATS_PERIODE, JOURNAUX } from './apiSocial';
import {
  useLectureSociale, EtatsLecture, FiltreEtats, FenetreActe, BandeauComptaDemo, Montant, motifValide, ChampMotif, ChoixRadio,
} from './communSocial';
import {
  MiseEnPlaceCompta, FenetreEcritureManuelle, FenetreAbandonnerEcriture, FenetreValiderAvecPiece, FenetreClotureExercice, exercicesDe,
} from './MiseEnPlaceFinance';
import { ETATS_EXERCICE } from './apiMiseEnPlace';

function LignesEcriture({ lignes }) {
  return (
    <table className="rh-tableau" style={{ margin: '6px 0 0', fontSize: 13 }}>
      <thead><tr><th>Compte</th><th>Libellé</th><th style={{ textAlign: 'right' }}>Débit</th><th style={{ textAlign: 'right' }}>Crédit</th></tr></thead>
      <tbody>
        {toArray(lignes).map((l) => (
          <tr key={l.rang}>
            <td>{l.compte}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{l.compte_libelle}</span></td>
            <td>{l.libelle || ''}</td>
            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.debit ? francs(l.debit) : ''}</td>
            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.credit ? francs(l.credit) : ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FenetreContrepasserEcriture({ ecriture, onFermer, onFait }) {
  const api = useSocial();
  const [motif, setMotif] = useState('');
  return (
    <FenetreActe titre={`Contrepasser l'écriture ${ecriture.numero}`} Icone={Undo2} libelleBouton="Proposer la contrepassation" danger pret={motifValide(motif)} onFermer={onFermer}
      aide="Une écriture validée ne se modifie jamais : la contrepassation passe l'écriture inverse, validée ensuite par une autre personne."
      onConfirmer={async (cle) => { await api.contrepasserEcriture(ecriture.id, motif.trim(), cle); onFait('Contrepassation proposée.'); }}>
      <ChampMotif valeur={motif} onChange={setMotif} />
    </FenetreActe>
  );
}

function Ecritures({ organisation, droits, comptes }) {
  const api = useSocial();
  const [journal, setJournal] = useState('');
  const [statut, setStatut] = useState('');
  const [page, setPage] = useState(1);
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const liste = useLectureSociale(() => api.ecritures(organisation.id, { page, taille: 25, journal, statut }), [api, organisation.id, page, journal, statut]);
  const elements = toArray(liste.donnees);
  const fait = (m) => { setFenetre(null); setInfo(m); liste.recharger(); };
  return (
    <>
      <div className="rh-outils" style={{ justifyContent: 'space-between' }}>
        <FiltreEtats table={ETATS_ECRITURE} codes={Object.keys(ETATS_ECRITURE)} valeur={statut} onChange={(c) => { setStatut(c); setPage(1); }} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select className="rh-champ-saisie" style={{ width: 'auto' }} aria-label="Journal" value={journal} onChange={(e) => { setJournal(e.target.value); setPage(1); }}>
            <option value="">Tous les journaux</option>
            {Object.entries(JOURNAUX).map(([c, l]) => <option key={c} value={c}>{l}</option>)}
          </select>
          {droits.a('ds_ecriture_saisir') && <Bouton variante="primaire" Icone={BookPlus} onClick={() => setFenetre({ type: 'saisir' })}>Écriture manuelle</Bouton>}
        </div>
      </div>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={liste} Icone={BookOpen} estVide={!elements.length} titreVide="Aucune écriture"
          vide={statut || journal ? 'Aucune écriture pour ces filtres.' : 'Les écritures naissent des décaissements, encaissements, arrêtés de caisse et retenues ; elles s\'affichent ici en lecture.'}>
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Écriture</th><th>Journal</th><th style={{ textAlign: 'right' }}>Montant</th><th>État</th><th aria-label="Actions" /></tr></thead>
              <tbody>
                {elements.map((e) => {
                  const total = toArray(e.lignes).reduce((s, l) => s + (Number(l.debit) || 0), 0);
                  const cleValider = e.contrepasse_ecriture_id ? 'ds_contrepassation_valider' : 'ds_ecriture_valider';
                  return (
                    <tr key={e.id}>
                      <td>
                        <details>
                          <summary style={{ cursor: 'pointer' }}><span className="rh-cellule-principale">{e.libelle}</span></summary>
                          <LignesEcriture lignes={e.lignes} />
                          {e.empreinte && <p className="rh-form-aide" style={{ margin: '6px 0 0' }} title={e.empreinte}>Empreinte {String(e.empreinte).slice(0, 16)}…</p>}
                        </details>
                        <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{e.numero || 'Brouillon, sans numéro'} · {dateLongue(e.date_ecriture)}{e.contrepasse_ecriture_id ? ' · contrepassation' : ''}</span>
                      </td>
                      <td>{libelle(JOURNAUX, e.journal)}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{moisLong(e.periode)}</span></td>
                      <td style={{ textAlign: 'right' }}><Montant n={total} /></td>
                      <td><Etat table={ETATS_ECRITURE} code={e.statut} /></td>
                      <td style={{ textAlign: 'right' }}>
                        {e.statut === 'BROUILLON' && droits.a(cleValider) && <Bouton petit variante="primaire" onClick={() => setFenetre({ type: 'valider', e })}>Valider</Bouton>}
                        {e.statut === 'BROUILLON' && droits.a(e.contrepasse_ecriture_id ? 'ds_contrepassation_proposer' : 'ds_ecriture_saisir') && <Bouton petit variante="discret" Icone={Trash2} onClick={() => setFenetre({ type: 'abandonner', e })}>Abandonner</Bouton>}
                        {e.statut === 'VALIDEE' && !e.contrepasse_ecriture_id && droits.a('ds_contrepassation_proposer') && <Bouton petit variante="discret" Icone={Undo2} onClick={() => setFenetre({ type: 'contrepasser', e })}>Contrepasser</Bouton>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PaginationDs donnees={liste.donnees} onPage={setPage} />
        </EtatsLecture>
      </section>
      {fenetre?.type === 'valider' && (
        <FenetreValiderAvecPiece organisation={organisation} ecriture={fenetre.e} onFermer={() => setFenetre(null)} onFait={fait}>
          <LignesEcriture lignes={fenetre.e.lignes} />
        </FenetreValiderAvecPiece>
      )}
      {fenetre?.type === 'saisir' && <FenetreEcritureManuelle organisation={organisation} comptes={comptes} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'abandonner' && <FenetreAbandonnerEcriture ecriture={fenetre.e} abandonner={api.abandonnerEcriture} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.type === 'contrepasser' && <FenetreContrepasserEcriture ecriture={fenetre.e} onFermer={() => setFenetre(null)} onFait={fait} />}
    </>
  );
}

function Balance({ referentiel }) {
  const lignes = toArray(referentiel.donnees?.balance).filter((l) => l.debit || l.credit);
  const td = lignes.reduce((s, l) => s + l.debit, 0);
  const tc = lignes.reduce((s, l) => s + l.credit, 0);
  return (
    <section className="rh-carte" style={{ padding: 0 }}>
      <EtatsLecture lecture={referentiel} Icone={Scale} estVide={!lignes.length} titreVide="Balance vide"
        vide={toArray(referentiel.donnees?.balance).length ? 'Aucune écriture validée : tous les comptes sont à zéro.' : 'Le plan de démonstration n\'est pas encore installé pour cette organisation.'}>
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Compte</th><th style={{ textAlign: 'right' }}>Débit</th><th style={{ textAlign: 'right' }}>Crédit</th><th style={{ textAlign: 'right' }}>Solde</th></tr></thead>
            <tbody>
              {lignes.map((l) => (
                <tr key={l.id}>
                  <td><span className="rh-cellule-principale">{l.numero}</span><span className="rh-cellule-secondaire" style={{ display: 'block' }}>{l.libelle}</span></td>
                  <td style={{ textAlign: 'right' }}><Montant n={l.debit} /></td>
                  <td style={{ textAlign: 'right' }}><Montant n={l.credit} /></td>
                  <td style={{ textAlign: 'right' }}><Montant n={l.solde} fort /></td>
                </tr>
              ))}
              <tr><td><strong>Total</strong></td><td style={{ textAlign: 'right' }}><Montant n={td} fort /></td><td style={{ textAlign: 'right' }}><Montant n={tc} fort /></td><td /></tr>
            </tbody>
          </table>
        </div>
      </EtatsLecture>
    </section>
  );
}

function FenetreCloture({ organisationId, periode, etape, onFermer, onFait }) {
  const api = useSocial();
  const [decision, setDecision] = useState('CONFIRMER');
  const [motif, setMotif] = useState('');
  if (etape === 'demander') {
    return (
      <FenetreActe titre={`Demander la clôture de ${moisLong(periode.mois)}`} Icone={Lock} libelleBouton="Demander la clôture" onFermer={onFermer}
        aide="Plus aucune écriture n'entre dans la période. Les brouillons doivent être validés ou abandonnés avant. Une autre personne confirme ensuite."
        onConfirmer={async (cle) => { await api.demanderCloture(organisationId, periode.mois, cle); onFait('Clôture demandée.'); }} />
    );
  }
  const refuser = decision === 'REFUSER';
  return (
    <FenetreActe titre={`Clôture de ${moisLong(periode.mois)}`} Icone={Lock} libelleBouton={refuser ? 'Refuser et rouvrir' : 'Confirmer la clôture'} danger={!refuser}
      pret={!refuser || motifValide(motif)} onFermer={onFermer}
      aide="La personne qui a demandé la clôture ne la confirme pas. Une période close l'est définitivement : une écriture tardive va sur la première période ouverte."
      onConfirmer={async (cle) => { await api.confirmerCloture(organisationId, periode.mois, refuser ? { decision, motif: motif.trim() } : { decision }, cle); onFait(refuser ? 'Clôture refusée : période rouverte.' : 'Période close.'); }}>
      <ChoixRadio nom="decision" options={{ CONFIRMER: 'Confirmer la clôture (irréversible)', REFUSER: 'Refuser et rouvrir la période' }} valeur={decision} onChange={setDecision} />
      {refuser && <ChampMotif valeur={motif} onChange={setMotif} />}
    </FenetreActe>
  );
}

function Periodes({ organisation, droits, referentiel }) {
  const [fenetre, setFenetre] = useState(null);
  const [info, setInfo] = useState('');
  const periodes = toArray(referentiel.donnees?.periodes);
  const exercices = exercicesDe(periodes);
  const fait = (m) => { setFenetre(null); setInfo(m); referentiel.recharger(); };
  return (
    <>
      {info && <BandeauInfo ton="succes">{info}</BandeauInfo>}
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={referentiel} Icone={Lock} estVide={!periodes.length} titreVide="Aucun exercice ouvert"
          vide="L'ouverture d'un exercice crée ses douze périodes ; elle suit l'installation du plan de démonstration (vue « Mise en place »).">
          {exercices.map((x) => (
            <div key={x.annee} style={{ padding: '12px 16px', borderBottom: '1px solid var(--sg-bord)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 8px' }}>
                <h3 style={{ margin: 0, fontSize: 14, display: 'flex', gap: 8, alignItems: 'center' }}>Exercice {x.annee} <Etat table={ETATS_EXERCICE} code={x.etat} /></h3>
                {x.etat === 'OUVERT' && droits.a('ds_periode_cloturer_demander') && <Bouton petit Icone={Lock} onClick={() => setFenetre({ exercice: x, etape: 'DEMANDER' })}>Demander la clôture de l'exercice</Bouton>}
                {x.etat === 'CLOTURE_DEMANDEE' && droits.a('ds_periode_cloturer_confirmer') && <Bouton petit variante="primaire" Icone={Lock} onClick={() => setFenetre({ exercice: x, etape: 'CONFIRMER' })}>Confirmer la clôture de l'exercice</Bouton>}
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                {x.periodes.map((p) => (
                  <li key={p.mois} style={{ border: '1px solid var(--sg-bord)', borderRadius: 6, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14 }}>{moisLong(p.mois)}<span style={{ display: 'block', marginTop: 2 }}><Etat table={ETATS_PERIODE} code={p.etat} /></span></span>
                    {p.etat === 'OUVERTE' && droits.a('ds_periode_cloturer_demander') && <Bouton petit onClick={() => setFenetre({ p, etape: 'demander' })}>Clôturer</Bouton>}
                    {p.etat === 'VALIDEE' && droits.a('ds_periode_cloturer_confirmer') && <Bouton petit variante="primaire" onClick={() => setFenetre({ p, etape: 'confirmer' })}>Décider</Bouton>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </EtatsLecture>
      </section>
      {fenetre?.p && <FenetreCloture organisationId={organisation.id} periode={fenetre.p} etape={fenetre.etape} onFermer={() => setFenetre(null)} onFait={fait} />}
      {fenetre?.exercice && <FenetreClotureExercice organisation={organisation} exercice={fenetre.exercice} etape={fenetre.etape} onFermer={() => setFenetre(null)} onFait={fait} />}
    </>
  );
}

const VUES = [['ecritures', 'Écritures'], ['balance', 'Balance'], ['periodes', 'Exercices et périodes'], ['mise-en-place', 'Mise en place']];

export default function ComptabiliteSection({ organisation, droits }) {
  const api = useSocial();
  const [params] = useSearchParams();
  const [vue, setVue] = useState(() => (VUES.some(([c]) => c === params.get('vue')) ? params.get('vue') : 'ecritures'));
  const referentiel = useLectureSociale(() => api.comptabilite(organisation.id), [api, organisation.id]);
  const journaux = toArray(referentiel.donnees?.journaux);
  const comptes = toArray(referentiel.donnees?.balance);
  const planAbsent = !!referentiel.donnees && !comptes.length;
  return (
    <>
      <BandeauComptaDemo />
      {referentiel.donnees?.mention && <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>{referentiel.donnees.mention}</p>}
      <div className="rh-vues rh-vues-ligne" role="group" aria-label="Vue de la comptabilité">
        {VUES.map(([c, l]) => <button key={c} type="button" className="rh-vue" aria-pressed={vue === c} onClick={() => setVue(c)}>{l}</button>)}
      </div>
      {journaux.length > 0 && vue === 'ecritures' && (
        <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>
          {journaux.map((j) => `${libelle(JOURNAUX, j.code)} : ${j.ecritures_validees} validée(s)${j.brouillons ? `, ${j.brouillons} brouillon(s)` : ''}`).join(' · ')}
        </p>
      )}
      {planAbsent && vue !== 'mise-en-place' && (
        <BandeauInfo ton="info" titre="Plan de démonstration non installé" action={<Bouton petit onClick={() => setVue('mise-en-place')}>Mise en place</Bouton>}>
          Installez le plan, puis ouvrez un exercice, avant toute écriture.
        </BandeauInfo>
      )}
      {vue === 'ecritures' && <Ecritures organisation={organisation} droits={droits} comptes={comptes} />}
      {vue === 'balance' && <Balance referentiel={referentiel} />}
      {vue === 'periodes' && <Periodes organisation={organisation} droits={droits} referentiel={referentiel} />}
      {vue === 'mise-en-place' && (referentiel.donnees ? <MiseEnPlaceCompta organisation={organisation} droits={droits} referentiel={referentiel} />
        : <section className="rh-carte" style={{ padding: 0 }}><EtatsLecture lecture={referentiel} estVide={false}>{null}</EtatsLecture></section>)}
    </>
  );
}
